import { useEffect, useState, useMemo } from 'react';
import { GridFilterModel, GridSortItem } from '@mui/x-data-grid';
import { useGlobalSearch } from 'hooks';
import { useAuth } from 'hooks/useAuth';
import { useLazyGetSupplierDevicesEsQuery } from 'store/services/device';
import { getEsDirectionFromMui } from 'store/services/es-query-helpers';
import { ElasticsearchDeviceBodyConfig } from 'types/ElasticsearchQueries';
import { DefaultEsQueryConfig } from 'lib/constants';
import { TanksPanelView } from './components/TanksPanelView/TanksPanelView';
import styles from './Accounts.module.scss';

export interface AccountsProps {
    /** A supplierId for the supplier that an admin user wants to view data for. */
    viewAsSupplier?: string;
}

export function Accounts({ viewAsSupplier }: AccountsProps) {
    const { user } = useAuth();
    const { globalSearchTerm } = useGlobalSearch();
    const [query, { data, isLoading, isFetching }] =
        useLazyGetSupplierDevicesEsQuery();
    const [currentPage, setCurrentPage] = useState<number>(0);
    const limit = DefaultEsQueryConfig.limit ?? 0;
    const [pageSize, setPageSize] = useState<number>(limit);
    const [sortModel, setSortModel] = useState<GridSortItem[]>([
        { field: 'lastReading.tank', sort: 'asc' },
    ]);
    const [searchModel, setSearchModel] = useState<GridFilterModel>();

    const [queryConfig, setQueryConfig] =
        useState<ElasticsearchDeviceBodyConfig>();

    const queryOptions = useMemo(
        () => ({
            currentPage,
            pageSize,
            sortModel,
            searchModel,
        }),
        [currentPage, pageSize, sortModel, searchModel]
    );

    useEffect(() => {
        setQueryConfig({
            limit: queryOptions.pageSize,
            offset: queryOptions.currentPage * queryOptions.pageSize,
            direction: getEsDirectionFromMui(
                queryOptions.sortModel[0]?.sort ?? ''
            ),
            sortField: queryOptions.sortModel[0]?.field ?? 'lastReading.time',
            search: globalSearchTerm ?? '',
            isAdminUser: user?.protected?.supplierId?.toString() === '0',
        });
    }, [queryOptions, globalSearchTerm, user?.protected?.supplierId]);
    useEffect(() => {
        setCurrentPage(0);
    }, [globalSearchTerm]);
    useEffect(() => {
        if (user?.protected) {
            query({
                userUid: user?.uid ?? '',
                supplierId: viewAsSupplier ?? user?.protected?.supplierId ?? '',
                queryConfig: queryConfig,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.protected, user?.uid, queryConfig]);

    return (
        <div data-testid="Tanks" className={styles.container}>
            {data && (
                <TanksPanelView
                    data={data}
                    loading={isLoading}
                    fetching={isFetching}
                    user={user}
                    onFilterModelChange={setSearchModel}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={setPageSize}
                    onSortModelChange={setSortModel}
                    sortModel={sortModel}
                    page={currentPage}
                />
            )}
        </div>
    );
}
